import { Health } from "./index"
import type {
	HealthRecordType,
	HealthChangeDelete,
	HealthChangeUpsert,
} from "./definitions"
import { useHealthStore } from "@/stores/health"

export type HealthChange = HealthChangeUpsert | HealthChangeDelete

export interface DrainOptions {
	pageSize?: number
	maxCycles?: number // safety limiter; default unlimited
}

export interface PollOptions extends DrainOptions {
	intervalMs?: number // default 30s
	onCycleError?: (err: unknown) => void
}

export async function ensureTokenAndSyncStore(
	tokenKey: string,
	recordTypes: HealthRecordType[]
): Promise<string> {
	const store = useHealthStore()
	// await store.loadTokens()
	const { token } = await Health.ensureChangesToken({ tokenKey, recordTypes })
	await store.setToken(tokenKey, token)
	return token
}

export async function refreshStoreFromNativeToken(
	tokenKey: string
): Promise<string | null> {
	const store = useHealthStore()
	// await store.loadTokens()
	const { token } = await Health.getStoredToken({ tokenKey })
	if (token) await store.setToken(tokenKey, token)
	else await store.removeToken(tokenKey)
	return token ?? null
}

export async function drainChanges(
    tokenKey: string,
    recordTypes: HealthRecordType[],
    onChange: (change: HealthChange) => Promise<void> | void,
    options: DrainOptions = {}
): Promise<{ total: number; cycles: number; lastToken?: string }> {
	const { pageSize, maxCycles } = options
	const store = useHealthStore()
	// await store.loadTokens()
    // Ensure native token exists and mirror to store
    const { token } = await Health.ensureChangesToken({ tokenKey, recordTypes })
    await store.setToken(tokenKey, token)

	let total = 0
	let cycles = 0
	// Health Connect can return multiple pages per getChanges call across invocations until hasMore=false
	// We loop until hasMore is false or we hit maxCycles
	// Note: pageSize is currently unused by native, but included for future API updates
	// eslint-disable-next-line no-constant-condition
    let lastToken: string | undefined
    while (true) {
        const res = await Health.getChanges({ tokenKey, recordTypes, pageSize, token: lastToken })
        for (const ch of res.changes) {
            await onChange(ch)
        }
        total += res.changes.length
        cycles += 1
        lastToken = (res.nextToken as any) || lastToken
        if (!res.hasMore) {
            // Do NOT persist next token here; let caller decide when to commit.
            return { total, cycles, lastToken }
        }
        if (maxCycles && cycles >= maxCycles) {
            // Do NOT persist next token here; let caller decide when to commit.
            return { total, cycles, lastToken }
        }
    }
}
